import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { currentUserHomepageSelector } from '../../redux/session/currentUser/selectors'

export type NavigationMode = 'app' | 'settings'

export const SETTINGS_ROOT = '/settings'

/** Settings contexts that aren't about the selected company, so the company select has no meaning there. */
const COMPANYLESS_SETTINGS_CONTEXTS = ['user', 'accountant', 'financier']

export const isSubPath = (pathname: string, path: string) => pathname === path || pathname.startsWith(`${path}/`)

/** The navigation mode is derived from the URL alone, so deep links and browser back always agree with it. */
export const useNavigationMode = () => {
  const { pathname } = useLocation()
  const mode: NavigationMode = isSubPath(pathname, SETTINGS_ROOT) ? 'settings' : 'app'
  const settingsContext = mode === 'settings' ? pathname.split('/')[2] : undefined

  return {
    mode,
    settingsContext,
    showCompanySelect: !settingsContext || !COMPANYLESS_SETTINGS_CONTEXTS.includes(settingsContext)
  }
}

const RETURN_PATH_KEY = 'finadeck:settingsReturnPath'

const readReturnPath = () => {
  try {
    return sessionStorage.getItem(RETURN_PATH_KEY)
  } catch {
    return null
  }
}

/**
 * Remembers the last app page visited, so "back to app" in settings returns there. Kept in
 * sessionStorage too, so it survives a reload while in settings. Falls back to the user's homepage.
 */
export const useReturnPath = (mode: NavigationMode) => {
  const { pathname, search } = useLocation()
  const homepage = useSelector(currentUserHomepageSelector)
  const returnPath = useRef(readReturnPath())

  useEffect(() => {
    if (mode !== 'app') return
    returnPath.current = `${pathname}${search}`
    try {
      sessionStorage.setItem(RETURN_PATH_KEY, returnPath.current)
    } catch {
      // Storage unavailable (e.g. private mode), the in-memory value still works for this session
    }
  }, [mode, pathname, search])

  return returnPath.current ?? homepage
}
